import { useCallback, useMemo, useState } from "react";
import {
    Button, TextField, Box, Typography, Grid, Autocomplete
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import Blade from "components/blade";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import strings from "strings";
import { isAgentLogin, isInsuranceLogin } from "tools/auth";
import { getNewRiskScores, getNewRiskScoresForAgent, getSubtagsForFilter } from "api";
import { reportException } from "tools/logs";
import LoadingWrapper from "components/loadingWrapper";
import { getCredentialStatuses } from "tools/helper";
import { FilterOptionsProps, FilterOptionType, RiskScoreSubTag, SortedCredential, SortedSubTag, SortedTag } from "./types";
import { RiskScoreDashboardV2Response } from "api/types/dashboard";

const t = strings.FILTERS

interface IFilterComponent {
    onFetchRiskScoreData?: (riskScoreCall: () => Promise<RiskScoreDashboardV2Response | undefined>) => void
    onHandleFilterSelection?: (selectedTags?: string[], includeUntagged?: boolean, credentialStatus?: string) => void
    riskScoresFilters: boolean
    carriersFilters: boolean
}

const FilterComponent: React.FC<IFilterComponent> = (props) => {
    const { onFetchRiskScoreData, onHandleFilterSelection, riskScoresFilters, carriersFilters } = props

    const tags: Record<string, string> = localStorage.getItem('tagCategories')
        ? JSON.parse(localStorage.getItem('tagCategories') as string)
        : {}

    const [openBlade, setOpenBlade] = useState(false)
    const [selectedTags, setSelectedTags] = useState<SortedTag[]>([])
    const [tagLoading, setTagLoading] = useState(false)
    const [subTags, setSubTags] = useState<RiskScoreSubTag>({})
    const [selectedSubTags, setSelectedSubTags] = useState<SortedSubTag[]>([])
    const [selectedCredentials, setSelectedCredentials] = useState<SortedCredential[]>([])
    const [includeUntagged, setIncludeUntagged] = useState(false)

    const isAgent = isAgentLogin()
    const isInsurance = isInsuranceLogin()

    const callSubTagsList = useCallback(async (selectedTags: SortedTag[], currentSelectedSubTags?: RiskScoreSubTag) => {
        const selectedTagIds = selectedTags.map((tag) => tag.id).join(',')
        try {
            const res = await getSubtagsForFilter(selectedTagIds)
            if (res && res?.tagSubCategories != null) {
                if (currentSelectedSubTags) {
                    const mergedSubTags = { ...currentSelectedSubTags, ...res.tagSubCategories }
                    setSubTags(mergedSubTags)
                } else {
                    setSubTags(res.tagSubCategories)
                }
            }
        } catch (error) {
            reportException(error)
        } finally {
            setTagLoading(false)
        }
    }, [])

    const handleTagCategorySelection = (selectedTags: SortedTag[]) => {
        setTagLoading(true)
        const regularTags = selectedTags.filter((tag) => tag.id !== t.UNTAGGED_DEFAULT_ID);

        if (regularTags.length) {
            callSubTagsList(regularTags)
        } else {
            setSubTags({})
            setSelectedSubTags([])
            setTagLoading(false)
        }
    }

    const tagArray = Object.entries(tags).map(([id, name]: [string, string]) => ({ id, name }))
    const sortedTags: SortedTag[] = useMemo(() => {
        return tagArray
            .map((tags) => {
                const firstLetter = tags.name[0].toUpperCase()
                return {
                    ...tags,
                    firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
                }
            })
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [])

    const sortedSubTags: SortedSubTag[] = useMemo(() => {
        if (!subTags || Object.keys(subTags).length === 0) return []
        return Object.entries(subTags)
            .map(([id, name]) => ({
                id,
                name,
                firstLetter: /[0-9]/.test(name[0].toUpperCase()) ? '0-9' : name[0].toUpperCase()
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [subTags])

    const sortedCredentials: SortedCredential[] = useMemo(() => {
        const credentialStatus = getCredentialStatuses()
        return credentialStatus
            .map((credentials) => {
                const firstLetter = credentials.status[0].toUpperCase()
                return {
                    ...credentials,
                    firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
                }
            })
            .sort((a, b) => a.status.localeCompare(b.status))
    }, [])

    const clearFilters = () => {
        setSelectedTags([])
        setSelectedCredentials([])
        setSelectedSubTags([])
        setSubTags({})
        setIncludeUntagged(false)
        if (riskScoresFilters && onHandleFilterSelection) {
            // Clear filters by passing empty array
            onHandleFilterSelection([], false, '')
        } else if (riskScoresFilters && onFetchRiskScoreData) {
            setTagLoading(true)
            onFetchRiskScoreData(isAgent ? getNewRiskScoresForAgent : getNewRiskScores)
            setTagLoading(false)
        }
    }

    const handleTagsFilter = (event: React.SyntheticEvent, newValue: SortedTag[]) => {
        const isUntagged = newValue.some((tag) => tag.id === t.UNTAGGED_DEFAULT_ID)
        setIncludeUntagged(isUntagged)
        setSelectedTags(newValue)
        handleTagCategorySelection(newValue)
    }

    const handleSubTagsFilter = (event: React.SyntheticEvent, newValue: SortedSubTag[]) => {
        setSelectedSubTags(newValue)
    }

    const handleCredentialsFilter = (event: React.SyntheticEvent, newValue: SortedCredential[]) => {
        setSelectedCredentials(newValue)
    }

    const onApplyFilters = () => {
        if ((riskScoresFilters || carriersFilters) && onHandleFilterSelection) {
            const selectedSubTagIds = selectedSubTags.map((tag) => tag.id)
            const statuses = selectedCredentials
                .map((credential) => credential.status)
                .join(',')
            onHandleFilterSelection(selectedSubTagIds, includeUntagged, statuses)
            setOpenBlade(false)
        }
    }

    const isApplyFiltersDisabled = () => {
        return !(
            (carriersFilters && selectedCredentials?.length > 0) ||
            (riskScoresFilters && includeUntagged && selectedTags?.length === 1 && selectedSubTags.length === 0) ||
            (riskScoresFilters && selectedTags?.length > 0 && selectedSubTags?.length > 0)
        )
    }

    const FilterOptions = <T extends FilterOptionType,>({ label, options, value, onChange, multiple, dataTestId }: FilterOptionsProps<T>) => (
        <Grid item xs={12} sm={12} display={"flex"} alignItems="start" flexDirection={"column"} gap={2} data-testid={dataTestId}>
            <Typography variant="body1">{label}</Typography>
            <Autocomplete
                multiple
                sx={{ width: "100%" }}
                options={options}
                groupBy={(option) => option.firstLetter}
                getOptionLabel={(option) => ('status' in option ? option.status : option.name)}
                filterSelectedOptions={!!multiple}
                value={value}
                onChange={onChange}
                componentsProps={dataTestId ? { popupIndicator: { 'data-testid': `${dataTestId}-popup` } as React.ComponentProps<typeof Autocomplete>['componentsProps'] extends { popupIndicator?: infer P } ? P : never } : undefined}
                ListboxProps={dataTestId ? { 'data-testid': `${dataTestId}-listbox` } as React.HTMLAttributes<HTMLUListElement> : undefined}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputProps={{
                            ...params.inputProps,
                            ...(dataTestId && { 'data-testid': `${dataTestId}-input` }),
                        }}
                        label={`Filter by ${label}`}
                        placeholder={`Select ${label}`}
                    />
                )}
                renderOption={(props, option) => {
                    const optionLabel = 'status' in option ? option.status : option.name
                    return (
                        <li
                            {...props}
                            {...(dataTestId && {
                                'data-testid': `${dataTestId}-option-${String(optionLabel).replace(/\s+/g, '-').toLowerCase()}`,
                            })}
                        >
                            {props.children ?? optionLabel}
                        </li>
                    )
                }}
            />
        </Grid>
    )

    const FiltersContent = () => {
        return (
            <LoadingWrapper isLoading={tagLoading}>
                <Box p={2}>
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={12} display={"flex"} alignItems="start" flexDirection={"column"}>
                            <Typography variant="h5">{t.TITLE}</Typography>
                            <Typography color={"gray"} variant="subtitle2">{t.DESCRIPTION}</Typography>
                        </Grid>
                        <Grid item xs={12} display="flex" gap={2} justifyContent={"end"}>
                            <Button
                                sx={{ borderRadius: 25 }}
                                startIcon={<Clear />}
                                variant="outlined"
                                color="error"
                                onClick={clearFilters}>
                                {t.CLEAR_BUTTON}
                            </Button>
                        </Grid>
                        {riskScoresFilters && (
                            <>
                                <FilterOptions
                                    label={t.TAGS_LABEL}
                                    options={sortedTags}
                                    value={selectedTags}
                                    onChange={handleTagsFilter}
                                    multiple={true}
                                    dataTestId="filter-tags"
                                />
                                {Object.keys(subTags).length > 0 && (
                                    <FilterOptions
                                        label={t.SUB_TAGS_LABEL}
                                        options={sortedSubTags}
                                        value={selectedSubTags}
                                        onChange={handleSubTagsFilter}
                                        multiple={true}
                                        dataTestId="filter-sub-tags"
                                    />
                                )}
                            </>
                        )}
                        {carriersFilters &&
                            <FilterOptions
                                label={t.CREDENTIALS_LABEL}
                                options={sortedCredentials}
                                value={selectedCredentials}
                                onChange={handleCredentialsFilter}
                                multiple={true}
                                dataTestId="filter-credentials"
                            />
                        }
                    </Grid>
                </Box>
            </LoadingWrapper>
        )
    }

    return (
        <>
            <Button
                sx={{ borderRadius: 25, minWidth: 120 }}
                variant="contained"
                color="primary"
                size="small" onClick={() => setOpenBlade(true)}
                startIcon={<FilterAltIcon />}
                disabled={isAgent}
                data-testid="filters-button"
            >
                {t.FILTERS}
            </Button>
            <Blade
                title={t.FILTERS}
                open={openBlade}
                onClose={() => {
                    setOpenBlade(false)
                }}
                bodyNode={<FiltersContent />}
                footerBtns={{
                    primary: {
                        id: "ApplyFiltersBtn",
                        'data-testid': 'apply-filters-button',
                        label: t.APPLY_BUTTON,
                        onClick: () => {
                            onApplyFilters()
                        },
                        disabled: isApplyFiltersDisabled()
                    },
                    secondary: {
                        id: "CloseFiltersBladeBtn",
                        'data-testid': 'close-filters-button',
                        label: t.CLOSE_BUTTON,
                        variant: 'outlined',
                        onClick: () => {
                            setOpenBlade(false)
                        },
                    }
                }}
            />
        </>
    )
}

export default FilterComponent